import { Injectable, inject } from '@angular/core';
import { IRanking, RankingType } from './models/ranking.model';
import { Observable, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { BadgeResponse } from './models/badges.response';
import { RankingResponse } from './models/ranking.response';
import { GamificationFactory } from './gamification.factory';
import { CategoryBadges } from './models/badges.model';
import { InfoDialogData, InfoDialogItem } from 'src/app/models/info-dialog';
import { ConfigService } from 'src/app/services/config.service';
import { ScoreDetail } from './models/score-detail.model';
import { ScoreDetailResponse } from './models/score-detail.response';
import { LocaleService } from 'src/app/services/locale.service';

@Injectable({
  providedIn: 'root',
})
export class GamificationService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);
  private readonly userService = inject(UserService);
  private readonly gamificationFactory = inject(GamificationFactory);
  private readonly configService = inject(ConfigService);
  private readonly localeService = inject(LocaleService);

  private readonly apiUrl = `${this.apiService.API_URL}/patrons/${this.userService.getUserId()}`;

  getRanking(): Observable<IRanking> {
    return this.http.get<RankingResponse>(`${this.apiUrl}/ranking`).pipe(
      map((ranking) => ({
        monthlyData: this.gamificationFactory.buildRanking(ranking, RankingType.MONTHLY),
        generalData: this.gamificationFactory.buildRanking(ranking, RankingType.GENERAL),
      }))
    );
  }

  getBadges(): Observable<CategoryBadges[]> {
    return this.http
      .get<BadgeResponse[]>(`${this.apiUrl}/badges`)
      .pipe(map((badges) => this.gamificationFactory.buildBadges(badges)));
  }

  getScores(date?: string): Observable<ScoreDetail> {
    const queryParams = {};

    if (date) {
      queryParams['month'] = date;
    }

    return this.http.get<ScoreDetailResponse>(`${this.apiUrl}/scoreDetail`, {
      params: queryParams,
    }).pipe(map((score) => this.gamificationFactory.buildScore(score)));
  }


  getGamificationInfoDialogData(): InfoDialogData {
    const moreInformation = this.configService.get().customLabels.ranking.moreInformation;

    const title = moreInformation?.['title'] ?? this.localeService.instant('user.ranking.moreInformation.title');
    const subtitle = moreInformation?.['subtitle'] ?? this.localeService.instant('user.ranking.moreInformation.subtitle');
    const toBorrow = moreInformation?.['toBorrow'] ?? this.localeService.instant('user.ranking.moreInformation.toBorrow');
    const toConsume = moreInformation?.['toConsume'] ?? this.localeService.instant('user.ranking.moreInformation.toConsume');
    const toValue = moreInformation?.['toValue'] ?? this.localeService.instant('user.ranking.moreInformation.toValue');
    const toComment = moreInformation?.['toComment'] ?? this.localeService.instant('user.ranking.moreInformation.toComment');

    const items: InfoDialogItem[] = [
      {
        description: toBorrow,
        iconLigature: 'loan',
      },
      {
        description: toConsume,
        iconLigature: 'clock',
      },
      {
        description: toValue,
        iconLigature: 'star',
      },
      {
        description: toComment,
        iconLigature: 'comment',
      },
    ];

    return {
      title: title,
      intro: subtitle,
      items: items,
    };
  }
}
